import React, { useCallback, useEffect, useMemo } from 'react';
import { Card, InputAdornment } from '@material-ui/core';
import { AttachMoney as AttachMoneyIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';
import {
  BaseSporCardProps,
  OrderQuoteItem,
  SupplierPurchaseOrder
} from '../../supplier/Supplier.interface';
import {
  convertEmptyToNull,
  handleFormNumber,
  useUpdateSpor
} from '../../supplier/Supplier.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

interface PricingForm {
  taxes: number | null | '';
  fees: number | null | '';
  shipping: number | null | '';
  totalCost: number | null | '';
  orderDate: Date | null;
}

const PricingDetailsFormCard: React.FC<BaseSporCardProps> = (
  props: BaseSporCardProps
) => {
  const { spor, onChange, onUpdate, onError } = props;
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Spor>(
    spor.task
  );
  const { updatedSpor, isUpdating, updateError, updateSpor } = useUpdateSpor(
    spor.id
  );

  const getTotalPrice = useCallback((): number => {
    const { quote: quotes = [] } = spor;
    return quotes.reduce((total: number, quote: OrderQuoteItem) => {
      return total + quote.price * (quote.quantity || 0);
    }, 0);
  }, [spor]);

  const computeTotal = useCallback(
    (updatedForm?: Partial<PricingForm>): number => {
      const newSpor: Partial<PricingForm> = {
        ...spor,
        ...updatedForm
      };
      return (
        getTotalPrice() +
        (newSpor.taxes || 0) +
        (newSpor.fees || 0) +
        (newSpor.shipping || 0)
      );
    },
    [spor, getTotalPrice]
  );

  const totalCost: number = useMemo(() => computeTotal(), [computeTotal]);

  // update only when totalCost changes
  useEffect(() => {
    if (spor.totalCost !== totalCost) {
      const dto: Partial<SupplierPurchaseOrder> = { totalCost };
      updateSpor(dto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCost]);

  useEffect(() => {
    if (updatedSpor) {
      onChange?.(updatedSpor);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSpor]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <AttachMoneyIcon />
        Section 4: Pricing
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          strict={false}
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={{
            taxes: handleFormNumber(spor.taxes),
            fees: handleFormNumber(spor.fees),
            shipping: handleFormNumber(spor.shipping),
            totalCost: handleFormNumber(spor.totalCost),
            orderDate: spor.orderDate || null
          }}
          onSubmit={(values: Partial<PricingForm>) => {
            // call onChange once with total value
            values = { ...values };
            convertEmptyToNull(
              values,
              'taxes',
              'fees',
              'shipping',
              'totalCost'
            );
            const totalCost: number = computeTotal(values);
            // allow null values
            const dto: Partial<SupplierPurchaseOrder> = {
              ...values,
              totalCost
            } as Partial<SupplierPurchaseOrder>;
            updateSpor(dto);
            onChange?.(dto);
          }}
        >
          <InlineForm>
            <InlineTextField
              type='number'
              name='taxes'
              label='Taxes'
              placeholder='Enter Taxes'
              disabled={isFieldDisabled('taxes')}
              inputProps={{
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                )
              }}
            />
            <InlineTextField
              type='number'
              name='fees'
              label='Fees'
              placeholder='Include any additional fees to be paid.'
              disabled={isFieldDisabled('fees')}
              inputProps={{
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                )
              }}
            />
            <InlineTextField
              type='number'
              name='shipping'
              label='Shipping'
              placeholder='Include the cost of shipping, if applicable.'
              disabled={isFieldDisabled('shipping')}
              inputProps={{
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                )
              }}
            />
            <InlineTextField
              type='number'
              name='totalCost'
              label='Total Cost'
              placeholder='Please enter the total cost of all items including taxes, fees and shipping.'
              disabled={isFieldDisabled('totalCost')}
              inputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                )
              }}
            />
            <InlineDatePicker
              name='orderDate'
              label='Order Date'
              placeholder='Enter Order Date'
              disabled={isFieldDisabled('orderDate')}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default PricingDetailsFormCard;
